<template>
  <section>
    <StatusBar v-if="!isCheckoutDesktopFlow" />
    <div :class="{ 'px-4': !isCheckoutDesktopFlow }">
      <h1 class="text-2xl md:text-4xl font-bold font-heading mb-2">Payment</h1>
      <p>All fields are required.</p>

      <div
        v-if="ui.error.code"
        id="error-display"
        class="p-2 bg-red-200 text-red-600 border border-red-400 my-4 rounded"
      >
        <template v-if="ui.error.code == 'table_unavailable'">
          Sorry, table is already reserved. Please select other tables
          <a @click.prevent="selectTables">here</a>.
        </template>

        <template v-else-if="ui.error.code === 'seating_time_full'">
          The seating time you selected is no longer available.
          <router-link :to="{ name: 'setSeatingTime' }" class="underline">
            Please go back to select a new seating time here.
          </router-link>
        </template>

        <template v-else>
          {{ ui.error.message }}
        </template>
      </div>

      <div class="mt-6 flex flex-col">
        <div class="w-full xl:w-1/2 ml-auto">
          <form @submit.prevent class="mb-8">
            <div class="flex flex-col space-y-2 xs:flex-row xs:space-y-0">
              <TextInput
                id="fname"
                forInput="fname-input"
                v-model="contact.first_name"
                label="First Name"
                classes="w-full xs:w-1/2 xs:pr-2"
              />
              <TextInput
                id="lname"
                forInput="lname-input"
                v-model="contact.last_name"
                label="Last Name"
                classes="w-full xs:w-1/2"
              />
            </div>
            <div class="flex flex-col space-y-2 mt-4 xs:flex-row xs:space-y-0 mb-8">
              <TextInput
                id="email"
                forInput="email-input"
                type="email"
                v-model="contact.email"
                label="Email"
                classes="w-full xs:w-3/5 xs:pr-2"
              />
              <TextInput
                id="tel"
                forInput="tel-input"
                type="tel"
                v-model="contact.phone"
                label="Phone"
                classes="w-full xs:w-2/5"
              />
            </div>
            <template v-if="isStripe && stripeEle">
              <StripeElements
                :stripeKey="stripeKey"
                :instanceOptions="stripeInstanceOptions"
                :elementsOptions="elementsOptions"
                ref="elms"
                id="payment-view-form"
              >
                <StripeElement />
              </StripeElements>
            </template>
            <div v-else-if="checkoutStore.pricing.total > 0.5">
              <p>Payment is not enabled for some reason.</p>
              {{ isStripe }}
              {{ stripeEle }}
            </div>
          </form>
          <CheckboxInput
            id="accepts-marketing"
            v-model="contact.accepts_marketing"
            :label="`Keep me informed of upcoming ${settingStore.venue_name} shows`"
            guest
          />
          <p v-show="checkoutText" class="text-sm text-fg-g-primary mt-7">
            {{ checkoutText }}
          </p>
          <div v-if="checkout_confirmation_text" class="mt-7">
            <CheckboxInput
              id="accepts-checkout-confirmation"
              v-model="acceptsCheckoutConfirmation"
              label="I agree to the following policy"
              guest
            />
            <div
              class="text-sm text-fg-g-primary mt-2 whitespace-pre-line"
              id="checkout-confirmation-text"
            >
              {{ checkout_confirmation_text }}
            </div>
          </div>
        </div>

        <div v-if="!isCheckoutDesktopFlow" class="price-information">
          <CheckoutDetailTable />
          <CheckoutPromoCode class="my-8" />
        </div>

        <CheckoutBottomBar
          :nextButtonText="saveButtonText"
          :isNextButtonDisabled="paying || !isFormValid"
          @click:next="onSave"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { StripeElementsOptions } from '@stripe/stripe-js/types/stripe-js/elements-group'
import { useCssVar } from '@vueuse/core'
import { mapState, mapStores } from 'pinia'
import { computed, defineComponent } from 'vue'
import { StripeElement, StripeElements } from 'vue-stripe-js'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import CheckboxInput from '@/common/components/molecules/CheckboxInput.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import { ROOM_MAP_BG_MODE } from '@/common/interfaces/RoomMap'
import CheckoutBottomBar from '@/modules/guestUi/components/organisms/CheckoutBottomBar.vue'
import CheckoutDetailTable from '@/modules/guestUi/components/organisms/CheckoutDetailTable.vue'
import CheckoutPromoCode from '@/modules/guestUi/components/organisms/CheckoutPromoCode.vue'
import StatusBar from '@/modules/guestUi/components/organisms/StatusBar.vue'
import { EnrichedAddOn } from '@/orders'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'
import { blankError, clearError, displayHandled } from '@/utils/errors'
import { sortBy } from '@/utils/utils'

import { FeatureFlag } from '@generated/types'

export default defineComponent({
  name: 'PaymentView',
  components: {
    CheckoutBottomBar,
    TextInput,
    CheckoutPromoCode,
    CheckoutDetailTable,
    ButtonDefault,
    CheckboxInput,
    StatusBar,
    StripeElement,
    StripeElements,
  },
  setup() {
    const isDarkMode = useCssVar('--mode')
    const colorPrimary = useCssVar('--primary-theme-color')
    const colorBackground = useCssVar('--g-bg-input')
    const colorText = useCssVar('--g-fg-primary')
    const borderColor = useCssVar('--g-border-color')
    const buttonStyle = { boxShadow: 'none', borderColor: borderColor.value }
    const buttonFocusStyle = {
      boxShadow: `0 0 0 3px ${colorText.value}`,
      borderColor: 'transparent',
    }
    const checkoutStore = useCheckoutStore()
    const elementsOptions = computed(() => {
      const clientSecret = checkoutStore.client_secret
      if (!clientSecret) return {}
      const theme = isDarkMode.value === ROOM_MAP_BG_MODE.FORCE_DARK ? 'night' : 'flat'
      return {
        clientSecret,
        appearance: {
          theme,
          variables: {
            colorPrimary: colorPrimary.value,
            colorBackground: colorBackground.value,
            colorText: colorText.value,
            borderRadius: '4px',
          },
          rules: {
            '.Tab': buttonStyle,
            '.Tab:focus': buttonFocusStyle,
            '.Input': buttonStyle,
            '.Input:focus': buttonFocusStyle,
          },
        },
      } as StripeElementsOptions
    })
    return { elementsOptions }
  },
  props: {},
  data() {
    return {
      acceptsCheckoutConfirmation: false,
      tooltipVisible: false,
      stripeKey: '',
      stripeInstanceOptions: {},
      paying: false,
      validation: {
        cardNumber: false,
        cardCVC: false,
        cardExpiry: false,
      },
      ui: {
        error: blankError(),
      },
    }
  },

  async created() {
    await this.settingStore.loading
    if (this.settingStore.public_key != 'pk_test_xxx') {
      // We want to avoid attempting to load stripe when we know we are running a test
      this.stripeKey = this.settingStore.public_key
    }
    if (this.settingStore.stripe_account) {
      this.stripeInstanceOptions = { stripeAccount: this.settingStore.stripe_account }
    }
  },

  computed: {
    ...mapStores(useSettingStore, useCheckoutStore, useUserStore),
    ...mapState(useCheckoutStore, ['add_ons', 'contact', 'hasPromoCodeApplied', 'performance']),
    ...mapState(useSettingStore, ['currency', 'checkout_confirmation_text']),

    checkoutText(): string {
      return this.settingStore.checkout_text
    },

    hasPerformanceAddOns(): boolean {
      return !!this.performance?.add_ons
    },

    isFormValid(): boolean {
      if (!this.isStripe) return this.checkoutStore.hasSufficientGuestInfo
      return (
        (!this.checkout_confirmation_text || this.acceptsCheckoutConfirmation) &&
        this.checkoutStore.hasSufficientGuestInfo
      )
    },

    isStripe(): boolean {
      return this.checkoutStore.pricing.total > 0.5 && this.settingStore.public_key != 'pk_test_xxx'
    },

    hasPromos(): boolean {
      return this.settingStore.flagEnabled(FeatureFlag.PROMO_CODES)
    },

    promoCodeDisplay(): string {
      const code = this.checkoutStore.promo_code
      if (!code) return ''
      return `${code.name} (${code.coupon.name})`
    },

    stripeEle(): boolean {
      return !!this.stripeKey
    },

    addOnsInTable(): EnrichedAddOn[] {
      return sortBy(this.checkoutStore.addOnsModels || [], 'name')
    },

    isCheckoutDesktopFlow(): boolean {
      return this.checkoutStore.isDesktopFlow
    },

    saveButtonText(): string {
      if (this.paying) return 'Processing...'
      return 'Purchase'
    },
  },

  methods: {
    async onBack(): Promise<void> {
      history.go(-1)
    },

    selectTables(): void {
      this.$router.push({ name: 'setTable' }).catch(() => {})
    },

    async onSave(): Promise<void> {
      if (this.paying || !this.isFormValid) return
      this.paying = true
      clearError(this.ui.error)

      try {
        const elms = this.$refs.elms as any
        elms?.elements.submit()
        await this.checkoutStore.checkout(async () => {
          return await elms?.instance.confirmPayment({
            elements: elms?.elements,
            clientSecret: this.checkoutStore.client_secret,
            redirect: 'if_required',
          })
        })
      } catch (error) {
        displayHandled(error, this.ui.error)
      } finally {
        this.paying = false
      }
    },
  },
})
</script>
